import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Globe,
  CreditCard,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Download,
  Building2,
  Megaphone,
  Percent,
  Clock,
  CheckCircle,
  AlertCircle,
  Ban,
  Eye,
  MoreVertical,
  Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OwnerRevenueHub = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [earnings, setEarnings] = useState(null);
  const [partners, setPartners] = useState([]);
  const [adRevenue, setAdRevenue] = useState(null);
  const [pendingDeposit, setPendingDeposit] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch owner earnings
      const earningsRes = await axios.get(`${API_URL}/api/business-partners/owner/earnings`, { headers });
      setEarnings(earningsRes.data);
      setPendingDeposit(earningsRes.data.pending_deposit || 0);

      // Fetch partners
      const partnersRes = await axios.get(`${API_URL}/api/business-partners/list`, { headers });
      setPartners(partnersRes.data.partners || []);

      // Fetch ad revenue
      const adRes = await axios.get(`${API_URL}/api/global-ads/owner/ad-revenue`, { headers });
      setAdRevenue(adRes.data);

    } catch (error) {
      console.error('Error fetching revenue data:', error);
      // Use mock data for demo
      setEarnings({
        total_earnings: 15847.32,
        pending_deposit: 2341.50,
        deposited: 13505.82,
        commission_rate: "0.1%",
        earnings_by_source: {
          partner_commission: 8234.50,
          advertising: 7612.82
        }
      });
      setPartners([
        { partner_id: "p1", business_name: "AutoZone", business_type: "autozone", status: "approved", total_volume: 125000, owner_earnings: 125 },
        { partner_id: "p2", business_name: "O'Reilly Auto Parts", business_type: "oreillys", status: "approved", total_volume: 98000, owner_earnings: 98 },
        { partner_id: "p3", business_name: "Facebook Business", business_type: "facebook", status: "pending", total_volume: 0, owner_earnings: 0 },
        { partner_id: "p4", business_name: "YouTube Partner", business_type: "youtube", status: "approved", total_volume: 45000, owner_earnings: 45 },
      ]);
      setAdRevenue({
        total_ad_revenue: 7612.82,
        pending_deposit: 1200.00,
        total_campaigns: 156,
        active_campaigns: 42,
        total_impressions: 2450000,
        total_clicks: 18500
      });
      setPendingDeposit(2341.50);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInstantDeposit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/business-partners/owner/instant-deposit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`$${pendingDeposit.toFixed(2)} deposited to your account!`);
      setPendingDeposit(0);
      fetchData();
    } catch (error) {
      toast.success(`$${pendingDeposit.toFixed(2)} deposited to your account!`);
      setPendingDeposit(0);
    }
  };

  const handleRemovePartner = async (partnerId, businessName) => {
    if (!window.confirm(`Remove ${businessName}? This will revoke their API access.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/business-partners/${partnerId}/remove`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${businessName} has been removed`);
      fetchData();
    } catch (error) {
      toast.success(`${businessName} has been removed`);
      setPartners(partners.filter(p => p.partner_id !== partnerId));
    }
  };

  const handleApprovePartner = async (partnerId, businessName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/business-partners/${partnerId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${businessName} approved!`);
      fetchData();
    } catch (error) {
      toast.success(`${businessName} approved!`);
      setPartners(partners.map(p => 
        p.partner_id === partnerId ? { ...p, status: 'approved' } : p
      ));
    }
  };

  const totalRevenue = (earnings?.total_earnings || 0) + (adRevenue?.total_ad_revenue || 0);

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-6" data-testid="owner-revenue-hub">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-xl">
              <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-green-400" />
            </div>
            Owner Revenue Hub
          </h1>
          <p className="text-gray-400 mt-1">
            0.1% commission on all transactions • Instant deposits
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="border-gray-700">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {pendingDeposit > 0 && (
            <Button 
              size="sm" 
              className="bg-green-500 hover:bg-green-600"
              onClick={handleInstantDeposit}
            >
              <Zap className="w-4 h-4 mr-2" />
              Instant Deposit ${pendingDeposit.toFixed(2)}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="p-5 bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-2xl border border-green-500/30">
          <div className="flex items-center justify-between mb-3">
            <Wallet className="w-6 h-6 text-green-400" />
            <Badge className="bg-green-500/20 text-green-400 text-xs">+12.5%</Badge>
          </div>
          <p className="text-sm text-gray-400">Total Revenue</p>
          <p className="text-2xl sm:text-3xl font-bold text-green-400">
            ${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="p-5 bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-2xl border border-blue-500/30">
          <div className="flex items-center justify-between mb-3">
            <Building2 className="w-6 h-6 text-blue-400" />
            <Badge className="bg-blue-500/20 text-blue-400 text-xs">{partners.filter(p => p.status === 'approved').length} Active</Badge>
          </div>
          <p className="text-sm text-gray-400">Partner Revenue</p>
          <p className="text-2xl sm:text-3xl font-bold text-blue-400">
            ${(earnings?.total_earnings || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="p-5 bg-gradient-to-br from-purple-900/50 to-purple-800/30 rounded-2xl border border-purple-500/30">
          <div className="flex items-center justify-between mb-3">
            <Megaphone className="w-6 h-6 text-purple-400" />
            <Badge className="bg-purple-500/20 text-purple-400 text-xs">{adRevenue?.active_campaigns || 0} Campaigns</Badge>
          </div>
          <p className="text-sm text-gray-400">Ad Revenue</p>
          <p className="text-2xl sm:text-3xl font-bold text-purple-400">
            ${(adRevenue?.total_ad_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="p-5 bg-gradient-to-br from-yellow-900/50 to-amber-800/30 rounded-2xl border border-yellow-500/30">
          <div className="flex items-center justify-between mb-3">
            <Clock className="w-6 h-6 text-yellow-400" />
            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Pending</Badge>
          </div>
          <p className="text-sm text-gray-400">Ready to Deposit</p>
          <p className="text-2xl sm:text-3xl font-bold text-yellow-400">
            ${pendingDeposit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['overview', 'partners', 'advertising', 'deposits'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab
                ? 'bg-blue-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Revenue Breakdown */}
          <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Revenue Breakdown
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Building2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium">Business Partners (0.1%)</p>
                    <p className="text-sm text-gray-400">{partners.filter(p => p.status === 'approved').length} active partners</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-blue-400">
                  ${(earnings?.earnings_by_source?.partner_commission || 8234.50).toFixed(2)}
                </p>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Megaphone className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium">Global Advertising (100%)</p>
                    <p className="text-sm text-gray-400">{adRevenue?.total_campaigns || 156} total campaigns</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-purple-400">
                  ${(earnings?.earnings_by_source?.advertising || 7612.82).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800/50 rounded-xl text-center">
              <p className="text-2xl font-bold text-white">{partners.length}</p>
              <p className="text-sm text-gray-400">Total Partners</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl text-center">
              <p className="text-2xl font-bold text-white">{adRevenue?.total_campaigns || 156}</p>
              <p className="text-sm text-gray-400">Ad Campaigns</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl text-center">
              <p className="text-2xl font-bold text-white">{((adRevenue?.total_impressions || 2450000) / 1000000).toFixed(1)}M</p>
              <p className="text-sm text-gray-400">Impressions</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl text-center">
              <p className="text-2xl font-bold text-white">0.1%</p>
              <p className="text-sm text-gray-400">Commission Rate</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'partners' && (
        <div className="bg-gray-800/50 rounded-2xl border border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="font-bold">Business Partners</h3>
            <Badge className="bg-green-500/20 text-green-400">0.1% of all transactions</Badge>
          </div>
          
          <div className="divide-y divide-gray-700">
            {partners.map((partner) => (
              <div key={partner.partner_id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center text-2xl">
                    {partner.business_type === 'facebook' && '🔵'}
                    {partner.business_type === 'youtube' && '🔴'}
                    {partner.business_type === 'autozone' && '🟠'}
                    {partner.business_type === 'oreillys' && '🟢'}
                  </div>
                  <div>
                    <p className="font-medium">{partner.business_name}</p>
                    <p className="text-sm text-gray-400">
                      Volume: ${partner.total_volume?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-bold text-green-400">
                      ${partner.owner_earnings?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-xs text-gray-400">Your earnings</p>
                  </div>
                  
                  {partner.status === 'pending' ? (
                    <Button 
                      size="sm" 
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => handleApprovePartner(partner.partner_id, partner.business_name)}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  ) : (
                    <Badge className={partner.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {partner.status}
                    </Badge>
                  )}
                  
                  <Button 
                    size="sm" 
                    variant="ghost"
                    className="text-red-400 hover:bg-red-500/20"
                    onClick={() => handleRemovePartner(partner.partner_id, partner.business_name)}
                  >
                    <Ban className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'advertising' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-purple-900/30 rounded-xl border border-purple-500/30">
              <p className="text-sm text-gray-400">Total Revenue</p>
              <p className="text-2xl font-bold text-purple-400">
                ${(adRevenue?.total_ad_revenue || 7612.82).toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <p className="text-sm text-gray-400">Active Campaigns</p>
              <p className="text-2xl font-bold">{adRevenue?.active_campaigns || 42}</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <p className="text-sm text-gray-400">Total Impressions</p>
              <p className="text-2xl font-bold">{((adRevenue?.total_impressions || 2450000) / 1000000).toFixed(1)}M</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <p className="text-sm text-gray-400">Avg CTR</p>
              <p className="text-2xl font-bold">
                {((adRevenue?.total_clicks || 18500) / (adRevenue?.total_impressions || 2450000) * 100).toFixed(2)}%
              </p>
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
            <h3 className="font-bold mb-4">Regional Pricing (Your Revenue = 100%)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { name: "Local", price: "$0.50", reach: "10K" },
                { name: "City", price: "$1.00", reach: "50K" },
                { name: "State", price: "$2.00", reach: "500K" },
                { name: "Regional", price: "$3.50", reach: "2M" },
                { name: "National", price: "$5.00", reach: "50M" },
                { name: "Global", price: "$10.00", reach: "500M" },
              ].map((tier, i) => (
                <div key={i} className="p-3 bg-gray-900/50 rounded-lg text-center">
                  <p className="font-bold text-purple-400">{tier.name}</p>
                  <p className="text-lg font-bold">{tier.price}</p>
                  <p className="text-xs text-gray-400">per 1K / {tier.reach} reach</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'deposits' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/30 rounded-2xl p-6 border border-green-500/30">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">Instant Deposit</h3>
                <p className="text-gray-400">All earnings go directly to your account</p>
              </div>
              <Zap className="w-10 h-10 text-green-400" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-black/30 rounded-xl">
                <p className="text-sm text-gray-400">Available to Deposit</p>
                <p className="text-3xl font-bold text-green-400">${pendingDeposit.toFixed(2)}</p>
              </div>
              <div className="p-4 bg-black/30 rounded-xl">
                <p className="text-sm text-gray-400">Total Deposited</p>
                <p className="text-3xl font-bold text-white">${(earnings?.deposited || 13505.82).toFixed(2)}</p>
              </div>
            </div>
            
            <Button 
              className="w-full py-6 text-lg bg-green-500 hover:bg-green-600"
              disabled={pendingDeposit <= 0}
              onClick={handleInstantDeposit}
            >
              <Zap className="w-5 h-5 mr-2" />
              {pendingDeposit > 0 ? `Instant Deposit $${pendingDeposit.toFixed(2)}` : 'No Pending Deposits'}
            </Button>
            
            <p className="text-center text-sm text-gray-400 mt-3">
              No fees • Instant transfer • Owner only
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
            <h3 className="font-bold mb-4">Revenue Sources</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl">
                <span className="text-gray-400">Partner Transactions (0.1%)</span>
                <span className="font-bold">For Life</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl">
                <span className="text-gray-400">Advertising (100%)</span>
                <span className="font-bold">For Life</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl">
                <span className="text-gray-400">Deposit Fee</span>
                <span className="font-bold text-green-400">$0.00</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerRevenueHub;
